import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Video, BookOpen, Settings, Mail, LogOut, 
  Plus, Edit, Trash2, Eye, Check, X, Save, Menu, Image,
  GraduationCap, Calendar, FileText, Briefcase, Clock, DollarSign, Users,
  Heart, ShoppingBag
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6a4505e5-3f70-4447-9214-c7fe0572ffa6/artifacts/4ongzfvs_THE%20MATRIX%20UNIVERSITY%20VIRTUAL%20REALITY%20LOGO.png";

// Login Component
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.user.is_admin) {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        onLogin(res.data.user);
      } else {
        setError('Admin access required');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md p-8 rounded-2xl glass-light"
      >
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="w-16 h-16 mx-auto mb-4 object-contain" />
          <h1 className="text-2xl font-serif font-bold text-white">Admin Login</h1>
          <p className="text-muted-foreground text-sm mt-2">Sign in to manage your website</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@matrixuniversity.com"
              required
              className="bg-muted border-border"
              data-testid="admin-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="bg-muted border-border"
              data-testid="admin-password"
            />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/20 text-destructive text-sm">
              {error}
            </div>
          )}

          <Button type="submit" disabled={isLoading} className="w-full btn-primary" data-testid="admin-submit">
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <p className="text-center text-muted-foreground text-sm mt-6">
          Default: admin@matrixuniversity.com / admin123
        </p>
      </motion.div>
    </div>
  );
};

// Video Management
const VideoManager = ({ token }) => {
  const [videos, setVideos] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    category: 'featured',
    page: 'home',
    order: 0,
    is_featured: false
  });

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingVideo) {
        await axios.put(`${API}/videos/${editingVideo.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/videos`, formData, { headers });
      }
      fetchVideos();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await axios.delete(`${API}/videos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVideos();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtube_url: video.youtube_url,
      category: video.category,
      page: video.page,
      order: video.order,
      is_featured: video.is_featured
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      youtube_url: '',
      category: 'featured',
      page: 'home',
      order: 0,
      is_featured: false
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Videos</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-video-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Video
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingVideo ? 'Edit Video' : 'Add New Video'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="bg-muted border-border"
                  data-testid="video-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Video URL (YouTube or MP4)</label>
                <Input
                  value={formData.youtube_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                  placeholder="https://youtu.be/... or https://example.com/video.mp4"
                  required
                  className="bg-muted border-border"
                  data-testid="video-url-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="bg-muted border-border resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Category</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="featured">Featured</SelectItem>
                      <SelectItem value="vr-chat">VR Chat</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="educational">Educational</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Page</label>
                  <Select value={formData.page} onValueChange={(v) => setFormData(prev => ({ ...prev, page: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Home</SelectItem>
                      <SelectItem value="vr-campus">VR Campus</SelectItem>
                      <SelectItem value="vr-chat-meta-quest">VR Chat Meta Quest</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                  />
                  <label className="text-sm text-white">Featured on Home</label>
                </div>
                <div>
                  <label className="text-sm text-white mr-2">Order:</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-muted border-border"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-video-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingVideo ? 'Update Video' : 'Add Video'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl glass-light overflow-hidden">
        <table className="table-modern">
          <thead>
            <tr>
              <th>Thumbnail</th>
              <th>Title</th>
              <th>Category</th>
              <th>Page</th>
              <th>Featured</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => (
              <tr key={video.id}>
                <td>
                  <img src={video.thumbnail} alt={video.title} className="w-24 h-14 object-cover rounded" />
                </td>
                <td className="max-w-xs truncate">{video.title}</td>
                <td><span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs">{video.category}</span></td>
                <td className="text-muted-foreground">{video.page}</td>
                <td>{video.is_featured ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-muted-foreground" />}</td>
                <td>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(video)} data-testid={`edit-video-${video.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(video.id)} data-testid={`delete-video-${video.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {videos.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">No videos yet. Add your first video!</div>
        )}
      </div>
    </div>
  );
};

// Book Management
const BookManager = ({ token }) => {
  const [books, setBooks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: 'Dr. Jason E. Williams, Ed.D.',
    description: '',
    cover_image: '',
    purchase_link: '',
    price: '',
    checkout_price: 0,
    order: 0
  });

  const fetchBooks = async () => {
    try {
      const res = await axios.get(`${API}/books`);
      setBooks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingBook) {
        await axios.put(`${API}/books/${editingBook.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/books`, formData, { headers });
      }
      fetchBooks();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/books/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchBooks();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      author: book.author,
      description: book.description,
      cover_image: book.cover_image,
      purchase_link: book.purchase_link,
      price: book.price || '',
      checkout_price: book.checkout_price || 0,
      order: book.order
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBook(null);
    setFormData({
      title: '',
      author: 'Dr. Jason E. Williams, Ed.D.',
      description: '',
      cover_image: '',
      purchase_link: '',
      price: '',
      checkout_price: 0,
      order: 0
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Books</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-book-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Book
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Title</label>
                <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required className="bg-muted border-border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Author</label>
                <Input value={formData.author} onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))} className="bg-muted border-border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3} className="bg-muted border-border resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Cover Image URL</label>
                <Input value={formData.cover_image} onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))} required className="bg-muted border-border" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Purchase Link</label>
                  <Input value={formData.purchase_link} onChange={(e) => setFormData(prev => ({ ...prev, purchase_link: e.target.value }))} required className="bg-muted border-border" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Price</label>
                  <Input value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} placeholder="$24.99" className="bg-muted border-border" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Checkout Price (numeric, for payment)</label>
                  <Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="24.99" className="bg-muted border-border" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary">
                <Save className="w-4 h-4 mr-2" />
                {editingBook ? 'Update Book' : 'Add Book'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {books.map((book) => (
          <div key={book.id} className="rounded-xl glass-light overflow-hidden">
            <img src={book.cover_image} alt={book.title} className="w-full h-48 object-cover" />
            <div className="p-4">
              <h3 className="font-semibold text-white mb-1 truncate">{book.title}</h3>
              <p className="text-sm text-muted-foreground mb-3">{book.price || 'No price set'}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(book)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(book.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {books.length === 0 && (
        <div className="p-8 text-center text-muted-foreground rounded-xl glass-light">No books yet. Add your first book!</div>
      )}
    </div>
  );
};

// Settings Manager
const SettingsManager = ({ token }) => {
  const [settings, setSettings] = useState({
    donate_stripe_link: '',
    donate_paypal_link: '',
    contact_email: '',
    hero_title: '',
    hero_subtitle: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(res => setSettings(res.data))
      .catch(console.error);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif font-bold text-white mb-6">Site Settings</h2>
      
      <div className="space-y-6 max-w-2xl">
        <div className="p-6 rounded-xl glass-light">
          <h3 className="text-lg font-semibold text-white mb-4">Hero Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Hero Title</label>
              <Input
                value={settings.hero_title}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_title: e.target.value }))}
                className="bg-muted border-border"
                data-testid="settings-hero-title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Hero Subtitle</label>
              <Textarea
                value={settings.hero_subtitle}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                rows={2}
                className="bg-muted border-border resize-none"
                data-testid="settings-hero-subtitle"
              />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl glass-light">
          <h3 className="text-lg font-semibold text-white mb-4">Donation Links</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Stripe Donation Link</label>
              <Input
                value={settings.donate_stripe_link}
                onChange={(e) => setSettings(prev => ({ ...prev, donate_stripe_link: e.target.value }))}
                placeholder="https://buy.stripe.com/..."
                className="bg-muted border-border"
                data-testid="settings-stripe-link"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">PayPal Donation Link</label>
              <Input
                value={settings.donate_paypal_link}
                onChange={(e) => setSettings(prev => ({ ...prev, donate_paypal_link: e.target.value }))}
                placeholder="https://paypal.me/..."
                className="bg-muted border-border"
                data-testid="settings-paypal-link"
              />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl glass-light">
          <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
          <div>
            <label className="block text-sm font-medium text-white mb-1">Contact Email</label>
            <Input
              type="email"
              value={settings.contact_email}
              onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
              className="bg-muted border-border"
              data-testid="settings-contact-email"
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={isSaving} className="btn-primary" data-testid="save-settings-btn">
          {isSaving ? 'Saving...' : saved ? 'Saved!' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Subscribers Manager
const SubscribersManager = ({ token }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    try {
      await axios.delete(`${API}/subscribers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSubscribers();
    } catch (err) {
      console.error(err);
    }
  };

  const exportEmails = () => {
    const emails = subscribers.map(s => s.email).join('\n');
    const blob = new Blob([emails], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-white">Email Subscribers</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''} from Coming Soon page
          </p>
        </div>
        {subscribers.length > 0 && (
          <Button onClick={exportEmails} variant="outline" data-testid="export-subscribers-btn">
            Export Emails
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground rounded-xl glass-light">Loading...</div>
      ) : (
        <div className="rounded-xl glass-light overflow-hidden">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Email</th>
                <th>Source</th>
                <th>Subscribed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((subscriber) => (
                <tr key={subscriber.id} data-testid={`subscriber-${subscriber.id}`}>
                  <td className="font-medium text-white">{subscriber.email}</td>
                  <td>
                    <span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs capitalize">
                      {subscriber.source.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="text-muted-foreground text-sm">{formatDate(subscriber.subscribed_at)}</td>
                  <td>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-destructive"
                      onClick={() => handleDelete(subscriber.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {subscribers.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No subscribers yet. Emails from the Coming Soon page will appear here.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Messages Manager
const MessagesManager = ({ token }) => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMessages(res.data))
      .catch(console.error);
  }, [token]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif font-bold text-white mb-6">Contact Messages</h2>
      
      <div className="space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`p-6 rounded-xl glass-light ${!message.is_read ? 'border-l-4 border-primary' : ''}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-white">{message.subject}</h3>
                <p className="text-sm text-muted-foreground">
                  From: {message.name} ({message.email})
                </p>
              </div>
              {!message.is_read && (
                <Button size="sm" variant="outline" onClick={() => markAsRead(message.id)}>
                  <Eye className="w-4 h-4 mr-1" /> Mark Read
                </Button>
              )}
            </div>
            <p className="text-muted-foreground whitespace-pre-wrap">{message.message}</p>
          </div>
        ))}
        {messages.length === 0 && (
          <div className="p-8 text-center text-muted-foreground rounded-xl glass-light">No messages yet.</div>
        )}
      </div>
    </div>
  );
};

// Gallery Manager
const GalleryManager = ({ token }) => {
  const [photos, setPhotos] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    category: 'vr-experience',
    order: 0
  });
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setPhotos(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/gallery`, formData, { headers });
      fetchPhotos();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPhotos();
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedPhotos.length} photos?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await Promise.all(selectedPhotos.map(id => 
        axios.delete(`${API}/gallery/${id}`, { headers })
      ));
      setSelectedPhotos([]);
      fetchPhotos();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleSelectPhoto = (id) => {
    setSelectedPhotos(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedPhotos.length === photos.length) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(photos.map(p => p.id));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      image_url: '',
      category: 'vr-experience',
      order: 0
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-white">Photo Gallery</h2>
          <p className="text-muted-foreground text-sm mt-1">{photos.length} photos in gallery</p>
        </div>
        <div className="flex gap-3">
          {selectedPhotos.length > 0 && (
            <Button 
              variant="outline" 
              className="text-destructive" 
              onClick={handleBulkDelete}
              data-testid="bulk-delete-photos"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedPhotos.length})
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-photo-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Photo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Photo</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Image URL</label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    required
                    className="bg-muted border-border"
                    data-testid="photo-url-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Title (optional)</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Photo description"
                    className="bg-muted border-border"
                    data-testid="photo-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Category</label>
                    <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                      <SelectTrigger className="bg-muted border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vr-experience">VR Experience</SelectItem>
                        <SelectItem value="events">Events</SelectItem>
                        <SelectItem value="campus">Campus</SelectItem>
                        <SelectItem value="community">Community</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Order</label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                      className="bg-muted border-border"
                    />
                  </div>
                </div>
                
                {/* Preview */}
                {formData.image_url && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-white mb-2">Preview</label>
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded-lg bg-muted"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                
                <Button type="submit" className="w-full btn-primary" data-testid="save-photo-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Add Photo
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Select All */}
      {photos.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={selectAll}
            className="text-sm text-muted-foreground hover:text-white transition-colors"
          >
            {selectedPhotos.length === photos.length ? 'Deselect All' : 'Select All'}
          </button>
          {selectedPhotos.length > 0 && (
            <span className="text-sm text-primary">{selectedPhotos.length} selected</span>
          )}
        </div>
      )}

      {/* Photo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {photos.map((photo) => (
          <div 
            key={photo.id} 
            className={`relative group rounded-xl overflow-hidden glass-light cursor-pointer ${
              selectedPhotos.includes(photo.id) ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => toggleSelectPhoto(photo.id)}
            data-testid={`gallery-photo-${photo.id}`}
          >
            <div className="aspect-square">
              <img 
                src={photo.image_url} 
                alt={photo.title || "Gallery photo"} 
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Checkbox overlay */}
            <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
              selectedPhotos.includes(photo.id) 
                ? 'bg-primary border-primary' 
                : 'border-white/50 bg-black/30 group-hover:border-white'
            }`}>
              {selectedPhotos.includes(photo.id) && <Check className="w-4 h-4 text-white" />}
            </div>
            
            {/* Delete button */}
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-destructive/80 hover:bg-destructive opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`delete-photo-${photo.id}`}
            >
              <Trash2 className="w-4 h-4 text-white" />
            </button>
            
            {/* Category badge */}
            <div className="absolute bottom-2 left-2 right-2">
              <span className="px-2 py-1 rounded-full bg-black/50 text-white text-xs truncate block">
                {photo.category}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {photos.length === 0 && (
        <div className="p-12 text-center text-muted-foreground rounded-xl glass-light">
          <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No photos in gallery yet.</p>
          <p className="text-sm mt-2">Add your first photo to get started!</p>
        </div>
      )}
    </div>
  );
};

// Course Manager
const CourseManager = ({ token }) => {
  const [courses, setCourses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    price: '',
    original_price: '',
    category: 'course',
    format: 'downloadable',
    duration: '',
    includes: [],
    purchase_link: '',
    zoom_link: '',
    is_featured: false,
    order: 0
  });
  const [includeInput, setIncludeInput] = useState('');

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      setCourses(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCourse) {
        await axios.put(`${API}/courses/${editingCourse.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/courses`, formData, { headers });
      }
      fetchCourses();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await axios.delete(`${API}/courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCourses();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      description: course.description,
      image_url: course.image_url,
      price: course.price,
      original_price: course.original_price || '',
      category: course.category,
      format: course.format,
      duration: course.duration || '',
      includes: course.includes || [],
      purchase_link: course.purchase_link || '',
      zoom_link: course.zoom_link || '',
      checkout_price: course.checkout_price || 0,
      is_featured: course.is_featured,
      order: course.order
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCourse(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      price: '',
      original_price: '',
      category: 'course',
      format: 'downloadable',
      duration: '',
      includes: [],
      purchase_link: '',
      zoom_link: '',
      checkout_price: 0,
      is_featured: false,
      order: 0
    });
    setIncludeInput('');
  };

  const addInclude = () => {
    if (includeInput.trim()) {
      setFormData(prev => ({ ...prev, includes: [...prev.includes, includeInput.trim()] }));
      setIncludeInput('');
    }
  };

  const removeInclude = (index) => {
    setFormData(prev => ({ ...prev, includes: prev.includes.filter((_, i) => i !== index) }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Courses</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-course-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Course
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="bg-muted border-border"
                  data-testid="course-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  required
                  className="bg-muted border-border resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Image URL</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  required
                  className="bg-muted border-border"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Price</label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="$99 or Free"
                    required
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Original Price</label>
                  <Input
                    value={formData.original_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value }))}
                    placeholder="$199"
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Checkout Price (numeric, for payment)</label>
                  <Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="99.99" className="bg-muted border-border" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Duration</label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="6 hours"
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Category</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="course">Course</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                      <SelectItem value="webinar">Webinar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Format</label>
                  <Select value={formData.format} onValueChange={(v) => setFormData(prev => ({ ...prev, format: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="downloadable">On-Demand</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Purchase Link</label>
                <Input
                  value={formData.purchase_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_link: e.target.value }))}
                  placeholder="https://..."
                  className="bg-muted border-border"
                />
              </div>
              {formData.format === 'live' && (
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Zoom/Meeting Link</label>
                  <Input
                    value={formData.zoom_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, zoom_link: e.target.value }))}
                    placeholder="https://zoom.us/..."
                    className="bg-muted border-border"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-white mb-1">What's Included</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={includeInput}
                    onChange={(e) => setIncludeInput(e.target.value)}
                    placeholder="Add item..."
                    className="bg-muted border-border"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                  />
                  <Button type="button" variant="outline" onClick={addInclude}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.includes.map((item, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removeInclude(i)} className="hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                  />
                  <label className="text-sm text-white">Featured</label>
                </div>
                <div>
                  <label className="text-sm text-white mr-2">Order:</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-muted border-border"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-course-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingCourse ? 'Update Course' : 'Add Course'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <div key={course.id} className="rounded-xl glass-light overflow-hidden" data-testid={`admin-course-${course.id}`}>
            <img src={course.image_url} alt={course.title} className="w-full h-40 object-cover" />
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-white truncate flex-1">{course.title}</h3>
                <span className="text-cta font-bold text-sm ml-2">{course.price}</span>
              </div>
              <div className="flex gap-2 mb-3">
                <span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs">{course.category}</span>
                <span className="px-2 py-1 rounded-full bg-secondary/20 text-secondary text-xs">{course.format}</span>
                {course.is_featured && <span className="px-2 py-1 rounded-full bg-cta/20 text-cta text-xs">Featured</span>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(course)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(course.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {courses.length === 0 && (
        <div className="p-8 text-center text-muted-foreground rounded-xl glass-light">No courses yet. Add your first course!</div>
      )}
    </div>
  );
};

// Event Manager
const EventManager = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    event_type: 'workshop',
    date: '',
    time: '',
    timezone: 'EST',
    duration: '',
    location: 'Online',
    platform: '',
    registration_link: '',
    price: 'Free',
    max_attendees: null,
    is_featured: false,
    order: 0
  });

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = { ...formData, max_attendees: formData.max_attendees || null };
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, data, { headers });
      } else {
        await axios.post(`${API}/events`, data, { headers });
      }
      fetchEvents();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEvents();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      image_url: event.image_url || '',
      event_type: event.event_type,
      date: event.date,
      time: event.time,
      timezone: event.timezone,
      duration: event.duration || '',
      location: event.location,
      platform: event.platform || '',
      registration_link: event.registration_link || '',
      price: event.price,
      checkout_price: event.checkout_price || 0,
      max_attendees: event.max_attendees,
      is_featured: event.is_featured,
      order: event.order
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      event_type: 'workshop',
      date: '',
      time: '',
      timezone: 'EST',
      duration: '',
      location: 'Online',
      platform: '',
      registration_link: '',
      price: 'Free',
      checkout_price: 0,
      max_attendees: null,
      is_featured: false,
      order: 0
    });
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Events</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="bg-muted border-border"
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  required
                  className="bg-muted border-border resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Image URL (optional)</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  className="bg-muted border-border"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Event Type</label>
                  <Select value={formData.event_type} onValueChange={(v) => setFormData(prev => ({ ...prev, event_type: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="workshop">Workshop</SelectItem>
                      <SelectItem value="webinar">Webinar</SelectItem>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="meetup">Meetup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Price</label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="Free or $99"
                    required
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Checkout Price (numeric, for payment)</label>
                  <Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="99.99" className="bg-muted border-border" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Date</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    required
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Time</label>
                  <Input
                    value={formData.time}
                    onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                    placeholder="2:00 PM"
                    required
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Timezone</label>
                  <Select value={formData.timezone} onValueChange={(v) => setFormData(prev => ({ ...prev, timezone: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EST">EST</SelectItem>
                      <SelectItem value="CST">CST</SelectItem>
                      <SelectItem value="MST">MST</SelectItem>
                      <SelectItem value="PST">PST</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Duration</label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="2 hours"
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Location</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Online or address"
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Platform</label>
                  <Input
                    value={formData.platform}
                    onChange={(e) => setFormData(prev => ({ ...prev, platform: e.target.value }))}
                    placeholder="Zoom, VR Chat, etc."
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Max Attendees</label>
                  <Input
                    type="number"
                    value={formData.max_attendees || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_attendees: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="Unlimited"
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Registration Link</label>
                <Input
                  value={formData.registration_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, registration_link: e.target.value }))}
                  placeholder="https://..."
                  className="bg-muted border-border"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                  />
                  <label className="text-sm text-white">Featured</label>
                </div>
                <div>
                  <label className="text-sm text-white mr-2">Order:</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-muted border-border"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-event-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingEvent ? 'Update Event' : 'Add Event'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl glass-light overflow-hidden">
        <table className="table-modern">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Date</th>
              <th>Price</th>
              <th>Featured</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id} data-testid={`admin-event-${event.id}`}>
                <td className="max-w-xs truncate">{event.title}</td>
                <td><span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs capitalize">{event.event_type}</span></td>
                <td className="text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(event.date)}
                  </div>
                </td>
                <td className={event.price === 'Free' ? 'text-green-400' : 'text-cta'}>{event.price}</td>
                <td>{event.is_featured ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-muted-foreground" />}</td>
                <td>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(event)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(event.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {events.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">No events yet. Add your first event!</div>
        )}
      </div>
    </div>
  );
};

// Ebook Manager
const EbookManager = ({ token }) => {
  const [ebooks, setEbooks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEbook, setEditingEbook] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: 'Dr. Jason E. Williams, Ed.D.',
    description: '',
    cover_image: '',
    price: '',
    download_link: '',
    purchase_link: '',
    pages: '',
    format: 'PDF',
    is_featured: false,
    order: 0
  });

  const fetchEbooks = async () => {
    try {
      const res = await axios.get(`${API}/ebooks`);
      setEbooks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEbooks();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingEbook) {
        await axios.put(`${API}/ebooks/${editingEbook.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/ebooks`, formData, { headers });
      }
      fetchEbooks();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this e-book?')) return;
    try {
      await axios.delete(`${API}/ebooks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEbooks();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (ebook) => {
    setEditingEbook(ebook);
    setFormData({
      title: ebook.title,
      author: ebook.author,
      description: ebook.description,
      cover_image: ebook.cover_image,
      price: ebook.price,
      checkout_price: ebook.checkout_price || 0,
      download_link: ebook.download_link || '',
      purchase_link: ebook.purchase_link || '',
      pages: ebook.pages || '',
      format: ebook.format,
      is_featured: ebook.is_featured,
      order: ebook.order
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEbook(null);
    setFormData({
      title: '',
      author: 'Dr. Jason E. Williams, Ed.D.',
      description: '',
      cover_image: '',
      price: '',
      checkout_price: 0,
      download_link: '',
      purchase_link: '',
      pages: '',
      format: 'PDF',
      is_featured: false,
      order: 0
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">E-Books</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-ebook-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add E-Book
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEbook ? 'Edit E-Book' : 'Add New E-Book'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="bg-muted border-border"
                  data-testid="ebook-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Author</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                  className="bg-muted border-border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  required
                  className="bg-muted border-border resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Cover Image URL</label>
                <Input
                  value={formData.cover_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))}
                  required
                  className="bg-muted border-border"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Price</label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="$19.99"
                    required
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Checkout Price (numeric)</label>
                  <Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="19.99" className="bg-muted border-border" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Pages</label>
                  <Input
                    value={formData.pages}
                    onChange={(e) => setFormData(prev => ({ ...prev, pages: e.target.value }))}
                    placeholder="120 pages"
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Format</label>
                  <Select value={formData.format} onValueChange={(v) => setFormData(prev => ({ ...prev, format: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PDF">PDF</SelectItem>
                      <SelectItem value="EPUB">EPUB</SelectItem>
                      <SelectItem value="MOBI">MOBI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Purchase Link</label>
                <Input
                  value={formData.purchase_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_link: e.target.value }))}
                  placeholder="https://..."
                  className="bg-muted border-border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Download Link</label>
                <Input
                  value={formData.download_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, download_link: e.target.value }))}
                  placeholder="https://..."
                  className="bg-muted border-border"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                  />
                  <label className="text-sm text-white">Featured</label>
                </div>
                <div>
                  <label className="text-sm text-white mr-2">Order:</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-muted border-border"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-ebook-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingEbook ? 'Update E-Book' : 'Add E-Book'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ebooks.map((ebook) => (
          <div key={ebook.id} className="rounded-xl glass-light overflow-hidden" data-testid={`admin-ebook-${ebook.id}`}>
            <img src={ebook.cover_image} alt={ebook.title} className="w-full h-48 object-cover" />
            <div className="p-4">
              <h3 className="font-semibold text-white mb-1 truncate">{ebook.title}</h3>
              <p className="text-sm text-muted-foreground mb-1">{ebook.author}</p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-cta font-bold">{ebook.price}</span>
                <span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs">{ebook.format}</span>
                {ebook.is_featured && <span className="px-2 py-1 rounded-full bg-cta/20 text-cta text-xs">Featured</span>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(ebook)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(ebook.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {ebooks.length === 0 && (
        <div className="p-8 text-center text-muted-foreground rounded-xl glass-light">No e-books yet. Add your first e-book!</div>
      )}
    </div>
  );
};

// Service Manager
const ServiceManager = ({ token }) => {
  const [services, setServices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'briefcase',
    price: '',
    duration: '',
    includes: [],
    booking_link: '',
    category: 'consulting',
    checkout_price: 0,
    is_featured: false,
    order: 0
  });
  const [includeInput, setIncludeInput] = useState('');

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/services`, formData, { headers });
      }
      fetchServices();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchServices();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      icon: service.icon,
      price: service.price,
      duration: service.duration || '',
      includes: service.includes || [],
      booking_link: service.booking_link || '',
      category: service.category,
      checkout_price: service.checkout_price || 0,
      is_featured: service.is_featured,
      order: service.order
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: 'briefcase',
      price: '',
      duration: '',
      includes: [],
      booking_link: '',
      category: 'consulting',
      checkout_price: 0,
      is_featured: false,
      order: 0
    });
    setIncludeInput('');
  };

  const addInclude = () => {
    if (includeInput.trim()) {
      setFormData(prev => ({ ...prev, includes: [...prev.includes, includeInput.trim()] }));
      setIncludeInput('');
    }
  };

  const removeInclude = (index) => {
    setFormData(prev => ({ ...prev, includes: prev.includes.filter((_, i) => i !== index) }));
  };

  const iconOptions = ['briefcase', 'phone', 'compass', 'mic', 'users', 'building', 'globe', 'star', 'award'];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Services</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="bg-muted border-border"
                  data-testid="service-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  required
                  className="bg-muted border-border resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Category</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consulting">Consulting</SelectItem>
                      <SelectItem value="speaking">Speaking</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Icon</label>
                  <Select value={formData.icon} onValueChange={(v) => setFormData(prev => ({ ...prev, icon: v }))}>
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map((icon) => (
                        <SelectItem key={icon} value={icon} className="capitalize">{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Price</label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="$150 or Free"
                    required
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Duration</label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="1 hour"
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Checkout Price (USD) - for Stripe/PayPal</label>
                <Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="150.00" className="bg-muted border-border" data-testid="service-checkout-price-input" />
                <p className="text-xs text-muted-foreground mt-1">Set to 0 to show "Contact for Pricing" instead of a purchase button</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Booking Link</label>
                <Input
                  value={formData.booking_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, booking_link: e.target.value }))}
                  placeholder="https://calendly.com/..."
                  className="bg-muted border-border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">What's Included</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={includeInput}
                    onChange={(e) => setIncludeInput(e.target.value)}
                    placeholder="Add item..."
                    className="bg-muted border-border"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                  />
                  <Button type="button" variant="outline" onClick={addInclude}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.includes.map((item, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm flex items-center gap-2">
                      {item}
                      <button type="button" onClick={() => removeInclude(i)} className="hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                  />
                  <label className="text-sm text-white">Featured</label>
                </div>
                <div>
                  <label className="text-sm text-white mr-2">Order:</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-muted border-border"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-service-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingService ? 'Update Service' : 'Add Service'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl glass-light overflow-hidden">
        <table className="table-modern">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Price</th>
              <th>Duration</th>
              <th>Featured</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.id} data-testid={`admin-service-${service.id}`}>
                <td className="max-w-xs truncate">{service.title}</td>
                <td><span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs capitalize">{service.category}</span></td>
                <td className={service.price === 'Free' ? 'text-green-400' : 'text-cta'}>{service.price}</td>
                <td className="text-muted-foreground">
                  {service.duration && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {service.duration}
                    </div>
                  )}
                </td>
                <td>{service.is_featured ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-muted-foreground" />}</td>
                <td>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(service)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(service.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {services.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">No services yet. Add your first service!</div>
        )}
      </div>
    </div>
  );
};

// Main Admin Page

// Sponsorship Manager
const SponsorshipManager = ({ token }) => {
  const [tiers, setTiers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({ title: '', price: '', checkout_price: 0, description: '', slogan: '', benefits: [], order: 0 });
  const [benefitInput, setBenefitInput] = useState('');

  const fetchTiers = async () => { try { const res = await axios.get(`${API}/sponsorship-tiers`); setTiers(res.data); } catch (err) { console.error(err); } };
  useEffect(() => { fetchTiers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingTier) {
        await axios.put(`${API}/sponsorship-tiers/${editingTier.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/sponsorship-tiers`, formData, { headers });
      }
      fetchTiers(); setIsDialogOpen(false); resetForm();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sponsorship tier?')) return;
    try { await axios.delete(`${API}/sponsorship-tiers/${id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchTiers(); } catch (err) { console.error(err); }
  };

  const openEdit = (tier) => {
    setEditingTier(tier);
    setFormData({ title: tier.title, price: tier.price, checkout_price: tier.checkout_price || 0, description: tier.description, slogan: tier.slogan || '', benefits: tier.benefits || [], order: tier.order });
    setIsDialogOpen(true);
  };

  const resetForm = () => { setEditingTier(null); setFormData({ title: '', price: '', checkout_price: 0, description: '', slogan: '', benefits: [], order: 0 }); setBenefitInput(''); };
  const addBenefit = () => { if (benefitInput.trim()) { setFormData(prev => ({ ...prev, benefits: [...prev.benefits, benefitInput.trim()] })); setBenefitInput(''); } };
  const removeBenefit = (index) => { setFormData(prev => ({ ...prev, benefits: prev.benefits.filter((_, i) => i !== index) })); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Sponsorship Tiers</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild><Button className="btn-primary" data-testid="add-sponsor-btn"><Plus className="w-4 h-4 mr-2" />Add Tier</Button></DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingTier ? 'Edit Tier' : 'Add Sponsorship Tier'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-white mb-1">Title</label><Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required className="bg-muted border-border" data-testid="sponsor-title-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-white mb-1">Display Price</label><Input value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} placeholder="$10,000+" required className="bg-muted border-border" /></div>
                <div><label className="block text-sm font-medium text-white mb-1">Checkout Price (USD)</label><Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="10000" className="bg-muted border-border" /></div>
              </div>
              <div><label className="block text-sm font-medium text-white mb-1">Slogan</label><Input value={formData.slogan} onChange={(e) => setFormData(prev => ({ ...prev, slogan: e.target.value }))} placeholder="Your impact tagline" className="bg-muted border-border" /></div>
              <div><label className="block text-sm font-medium text-white mb-1">Description</label><Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3} required className="bg-muted border-border resize-none" /></div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Benefits</label>
                <div className="flex gap-2 mb-2"><Input value={benefitInput} onChange={(e) => setBenefitInput(e.target.value)} placeholder="Add benefit..." className="bg-muted border-border" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())} /><Button type="button" variant="outline" onClick={addBenefit}>Add</Button></div>
                <div className="flex flex-wrap gap-2">{formData.benefits.map((b, i) => (<span key={i} className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm flex items-center gap-2">{b}<button type="button" onClick={() => removeBenefit(i)} className="hover:text-white"><X className="w-3 h-3" /></button></span>))}</div>
              </div>
              <div><label className="text-sm text-white mr-2">Order:</label><Input type="number" value={formData.order} onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))} className="w-20 bg-muted border-border inline-block" /></div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-sponsor-btn"><Save className="w-4 h-4 mr-2" />{editingTier ? 'Update Tier' : 'Add Tier'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-4">
        {tiers.map((tier) => (
          <div key={tier.id} className="p-4 rounded-xl glass-light flex items-start justify-between" data-testid={`admin-sponsor-${tier.id}`}>
            <div>
              <h3 className="text-lg font-semibold text-white">{tier.title}</h3>
              <p className="text-cta font-bold">{tier.price}</p>
              {tier.slogan && <p className="text-sm text-secondary italic">{tier.slogan}</p>}
              <p className="text-sm text-muted-foreground mt-1">{tier.benefits?.length || 0} benefits</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(tier)}><Edit className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDelete(tier.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {tiers.length === 0 && <p className="text-muted-foreground text-center py-8">No sponsorship tiers yet</p>}
      </div>
    </div>
  );
};

// Merchandise Manager
const MerchandiseManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', price: '', checkout_price: 0, image_url: '', category: 'apparel', in_stock: true, order: 0 });

  const fetchItems = async () => { try { const res = await axios.get(`${API}/merchandise`); setItems(res.data); } catch (err) { console.error(err); } };
  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingItem) {
        await axios.put(`${API}/merchandise/${editingItem.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/merchandise`, formData, { headers });
      }
      fetchItems(); setIsDialogOpen(false); resetForm();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await axios.delete(`${API}/merchandise/${id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchItems(); } catch (err) { console.error(err); }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title, description: item.description, price: item.price, checkout_price: item.checkout_price || 0, image_url: item.image_url || '', category: item.category || 'apparel', in_stock: item.in_stock !== false, order: item.order });
    setIsDialogOpen(true);
  };

  const resetForm = () => { setEditingItem(null); setFormData({ title: '', description: '', price: '', checkout_price: 0, image_url: '', category: 'apparel', in_stock: true, order: 0 }); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-white">Merchandise</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild><Button className="btn-primary" data-testid="add-merch-btn"><Plus className="w-4 h-4 mr-2" />Add Item</Button></DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingItem ? 'Edit Item' : 'Add Merchandise'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-white mb-1">Title</label><Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required className="bg-muted border-border" data-testid="merch-title-input" /></div>
              <div><label className="block text-sm font-medium text-white mb-1">Description</label><Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3} required className="bg-muted border-border resize-none" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-white mb-1">Display Price</label><Input value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} placeholder="$44" required className="bg-muted border-border" /></div>
                <div><label className="block text-sm font-medium text-white mb-1">Checkout Price (USD)</label><Input type="number" step="0.01" value={formData.checkout_price} onChange={(e) => setFormData(prev => ({ ...prev, checkout_price: parseFloat(e.target.value) || 0 }))} placeholder="44.00" className="bg-muted border-border" /></div>
              </div>
              <div><label className="block text-sm font-medium text-white mb-1">Image URL</label><Input value={formData.image_url} onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))} placeholder="https://..." className="bg-muted border-border" /></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={formData.in_stock} onCheckedChange={(v) => setFormData(prev => ({ ...prev, in_stock: v }))} />
                  <label className="text-sm text-white">In Stock</label>
                </div>
                <div><label className="text-sm text-white mr-2">Order:</label><Input type="number" value={formData.order} onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))} className="w-20 bg-muted border-border inline-block" /></div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-merch-btn"><Save className="w-4 h-4 mr-2" />{editingItem ? 'Update Item' : 'Add Item'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <div key={item.id} className="rounded-xl glass-light overflow-hidden" data-testid={`admin-merch-${item.id}`}>
            {item.image_url ? (
              <div className="aspect-video bg-black"><img src={item.image_url} alt={item.title} className="w-full h-full object-contain" /></div>
            ) : (
              <div className="aspect-video bg-muted flex items-center justify-center"><ShoppingBag className="w-12 h-12 text-muted-foreground" /></div>
            )}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="text-cta font-bold">{item.price}</p>
              <p className="text-sm text-muted-foreground">{item.in_stock ? 'In Stock' : 'Out of Stock'}</p>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => openEdit(item)}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-muted-foreground text-center py-8 col-span-full">No merchandise yet</p>}
      </div>
    </div>
  );
};


export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('videos');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const token = localStorage.getItem('token');

  if (!user) {
    return <LoginForm onLogin={setUser} />;
  }

  const tabs = [
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'books', label: 'Books', icon: BookOpen },
    { id: 'courses', label: 'Memberships', icon: GraduationCap },
    { id: 'ebooks', label: 'E-Books', icon: FileText },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'sponsorship', label: 'Sponsorship', icon: Heart },
    { id: 'merchandise', label: 'Merchandise', icon: ShoppingBag },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'subscribers', label: 'Subscribers', icon: Users },
    { id: 'messages', label: 'Messages', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10 object-contain" />
            <div>
              <h2 className="font-semibold text-white">Admin Panel</h2>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === tab.id ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted mb-2"
          >
            <Eye className="w-5 h-5" />
            View Site
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border p-4 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-muted rounded-lg">
          <Menu className="w-6 h-6 text-white" />
        </button>
        <h2 className="font-semibold text-white">Admin Panel</h2>
        <div className="w-10" />
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-8 pt-20 lg:pt-8 overflow-auto">
        {activeTab === 'videos' && <VideoManager token={token} />}
        {activeTab === 'books' && <BookManager token={token} />}
        {activeTab === 'courses' && <CourseManager token={token} />}
        {activeTab === 'ebooks' && <EbookManager token={token} />}
        {activeTab === 'events' && <EventManager token={token} />}
        {activeTab === 'services' && <ServiceManager token={token} />}
        {activeTab === 'sponsorship' && <SponsorshipManager token={token} />}
        {activeTab === 'merchandise' && <MerchandiseManager token={token} />}
        {activeTab === 'gallery' && <GalleryManager token={token} />}
        {activeTab === 'subscribers' && <SubscribersManager token={token} />}
        {activeTab === 'messages' && <MessagesManager token={token} />}
        {activeTab === 'settings' && <SettingsManager token={token} />}
      </main>
    </div>
  );
}
